export interface CanonicalClientRecord {
  clientId: string;
  clientNumber: string;
  legalName: string;
  displayName: string;
  status: string;
  currency: string;
  timezone: string;
  paymentReportingEnabled: boolean;
  clientPortalEnabled: boolean;
  statementServicesEnabled: boolean;
  recoveryServicesEnabled: boolean;
  externalReference: string | null;
  dataQualityStatus: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

export interface CanonicalClientListResult {
  companyId: string;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  items: CanonicalClientRecord[];
  capabilities: ClientManagementCapabilities;
}

export interface ClientManagementCapabilities {
  create: boolean;
  edit: boolean;
  status: boolean;
  archive: boolean;
  contactsManage?: boolean;
  configurationManage?: boolean;
  assignmentsManage?: boolean;
  notesCreate?: boolean;
  notesManage?: boolean;
  auditRead?: boolean;
}

export type ClientAssignmentRole = "PRIMARY_RECOVERY_SPECIALIST" | "BACKUP_RECOVERY_SPECIALIST" | "CLIENT_SUCCESS_MANAGER" | "MANAGER" | "SUPERVISOR";
export type ClientNoteType = "GENERAL" | "ONBOARDING" | "SERVICE" | "OPERATIONS" | "MANAGEMENT" | "ESCALATION";

export interface ClientAssignmentRecord {
  assignmentId: string; clientId: string; assigneeType: "USER"; assigneeUserId: string; assigneeName: string; assigneeEmail: string | null;
  assignmentRole: ClientAssignmentRole; status: "ACTIVE" | "INACTIVE" | "ARCHIVED"; effectiveAt: string; expiresAt: string | null;
  isPrimary: boolean; isCurrent: boolean; version: number; createdAt: string; updatedAt: string; archivedAt: string | null;
}

export interface AvailableClientAssignee { userId: string; name: string; email: string; }

export interface ClientAssignmentInput {
  assigneeUserId: string; assignmentRole: ClientAssignmentRole; status: "ACTIVE" | "INACTIVE" | "ARCHIVED"; effectiveAt: string; expiresAt: string;
}

export interface ClientNoteRecord {
  noteId: string; clientId: string; noteType: ClientNoteType; body: string; visibility: "INTERNAL"; status: "ACTIVE" | "ARCHIVED";
  authorName: string; createdBy: string; createdAt: string; updatedBy: string; updatedAt: string; version: number; archivedAt: string | null;
}

export interface ClientNoteInput { noteType: ClientNoteType; body: string; status: "ACTIVE" | "ARCHIVED"; }

export interface ClientHistoryRecord {
  historyId: string; timestamp: string; actor: string; event: string; resourceType: string; resourceId: string; summary: string;
  changedFields: string[]; status: string | null; version: number | null; correlationId: string;
}

export type ClientContactEligibility = "GENERAL" | "BILLING" | "RECOVERY" | "STATEMENT_REPORT" | "LETTER" | "CUSTOMER_INVOICE" | "ESCALATION" | "OPERATIONAL";

export interface ClientContactRecord {
  contactId: string;
  clientId: string;
  contactName: string;
  jobTitle: string | null;
  department: string | null;
  email: string | null;
  phone: string | null;
  preferredContactMethod: "EMAIL" | "PHONE" | "NONE";
  status: "ACTIVE" | "INACTIVE" | "ARCHIVED";
  isDefault: boolean;
  platformUserId: string | null;
  eligibilities: ClientContactEligibility[];
  version: number;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

export interface ClientContactInput {
  contactName: string;
  jobTitle: string;
  department: string;
  email: string;
  phone: string;
  preferredContactMethod: "EMAIL" | "PHONE" | "NONE";
  isDefault: boolean;
  platformUserId: string;
  eligibilities: ClientContactEligibility[];
}

export interface ClientAddressRecord {
  addressId: string;
  clientId: string;
  addressType: "CORRESPONDENCE" | "REMITTANCE";
  addressee: string | null;
  addressLine1: string;
  addressLine2: string | null;
  locality: string;
  region: string;
  postalCode: string;
  country: string;
  isDefault: boolean;
  status: "ACTIVE" | "INACTIVE" | "ARCHIVED";
  sourceType: "AR_CLIENT_MANAGED" | "PLATFORM_REFERENCE";
  platformAddressReference: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

export interface ClientAddressInput {
  addressType: "CORRESPONDENCE" | "REMITTANCE";
  addressee: string;
  addressLine1: string;
  addressLine2: string;
  locality: string;
  region: string;
  postalCode: string;
  country: string;
  isDefault: boolean;
  sourceType: "AR_CLIENT_MANAGED" | "PLATFORM_REFERENCE";
  platformAddressReference: string;
}

export interface ClientServiceConfiguration {
  configurationId: string | null;
  statementHandlingMode: "AR_OS_MANAGED" | "AUTHORIZED_CLIENT_SYSTEM_ACCESS" | "CONNECTED_ERP_SYSTEM";
  statementSystemAuthorityReference: string | null;
  statementSystemAuthorityStatus: "NOT_CONFIGURED" | "PENDING" | "AUTHORIZED" | "CONNECTED" | "REVOKED" | "UNAVAILABLE";
  customerInvoiceHandlingMode: "AUTHORIZED_CLIENT_SYSTEM_ACCESS" | "CONNECTED_ERP_SYSTEM" | "CLIENT_MANAGED";
  customerInvoiceSystemAuthorityReference: string | null;
  customerInvoiceSystemAuthorityStatus: "NOT_CONFIGURED" | "PENDING" | "AUTHORIZED" | "CONNECTED" | "REVOKED" | "UNAVAILABLE";
  letterDeliveryMode: "COLLABRECOVER_MANAGED" | "CLIENT_MANAGED";
  version: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export type ClientPresentationReferenceField =
  | "letterPresentationReference"
  | "statementPresentationReference"
  | "senderDisplayReference"
  | "portalPresentationReference";

export interface ClientPresentationConfiguration {
  configurationId: string | null;
  letterPresentationReference: string | null;
  statementPresentationReference: string | null;
  senderDisplayReference: string | null;
  portalPresentationReference: string | null;
  version: number;
  updatedAt: string | null;
}

export interface ClientPresentationProfileOption {
  profileId: string;
  label: string;
  authority: "SHARED_PLATFORM_BRANDING";
  status: "AVAILABLE";
  updatedAt: string;
}

export interface ResolvedClientPresentationReference {
  configured: boolean;
  status: "AVAILABLE" | "UNAVAILABLE" | "AUTHORITY_UNAVAILABLE" | "NOT_CONFIGURED";
  label: string | null;
}

export interface ClientPortalAssociationProjection {
  enabled: boolean;
  authorityStatus: "AVAILABLE" | "UNAVAILABLE";
  associationStatus: "ASSOCIATED" | "NO_ASSOCIATION_FOUND" | "UNAVAILABLE";
  configurationStatus: string;
  entitlementStatus: "NOT_AUTHORITATIVELY_AVAILABLE";
  lastUpdatedAt: string | null;
}

export interface ClientBrandingPortalResult {
  companyId: string;
  clientId: string;
  configuration: ClientPresentationConfiguration;
  brandingAuthority: { status: "AVAILABLE" | "UNAVAILABLE" };
  resolvedReferences: Record<ClientPresentationReferenceField, ResolvedClientPresentationReference>;
  availableProfiles: ClientPresentationProfileOption[];
  portalAssociation: ClientPortalAssociationProjection;
  capabilities: Pick<ClientManagementCapabilities, "configurationManage">;
}

export interface ClientCoreInput {
  legalName: string;
  displayName: string;
  clientType: "RECOVERY_CLIENT";
  defaultCurrency: string;
  timeZone: string;
  paymentReportingEnabled: boolean;
  clientPortalEnabled: boolean;
  statementServicesEnabled: boolean;
  recoveryServicesEnabled: boolean;
  externalSystemReference: string;
}

export interface CanonicalClientListInput {
  page?: number;
  pageSize?: 25 | 50 | 100;
  search?: string;
  status?: string;
  sort?: "name" | "number" | "status" | "created" | "updated";
  direction?: "asc" | "desc";
}

export async function listCanonicalClients(input: CanonicalClientListInput = {}) {
  const params = new URLSearchParams({
    page: String(input.page || 1),
    pageSize: String(input.pageSize || 25),
    sort: input.sort || "name",
    direction: input.direction || "asc",
  });
  if (input.search) params.set("search", input.search);
  if (input.status) params.set("status", input.status);
  return request<CanonicalClientListResult>(`/api/ar-recovery/master-data/clients?${params}`);
}

export async function getCanonicalClient(clientId: string, includeArchived = false) {
  const params = includeArchived ? "?includeArchived=true" : "";
  return request<{ companyId: string; client: CanonicalClientRecord; capabilities: ClientManagementCapabilities }>(
    `/api/ar-recovery/master-data/clients/${encodeURIComponent(clientId)}${params}`,
  );
}

export function createCanonicalClient(input: ClientCoreInput, idempotencyKey: string) {
  return request<{ clientId: string; clientNumber: string; status: "INACTIVE"; version: number }>(
    "/api/ar-recovery/master-data/clients",
    { method: "POST", headers: { "Content-Type": "application/json", "Idempotency-Key": idempotencyKey }, body: JSON.stringify(input) },
  );
}

export function editCanonicalClient(clientId: string, input: Partial<ClientCoreInput> & { expectedVersion: number }) {
  return request<{ clientId: string; version: number; correlationId: string }>(
    `/api/ar-recovery/master-data/clients/${encodeURIComponent(clientId)}`,
    { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(input) },
  );
}

export function transitionCanonicalClient(clientId: string, status: "ACTIVE" | "INACTIVE" | "ARCHIVED", expectedVersion: number) {
  return request<{ clientId: string; version: number; status: string; correlationId: string }>(
    `/api/ar-recovery/master-data/clients/${encodeURIComponent(clientId)}/status`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status, expectedVersion }) },
  );
}

export function listClientContacts(clientId: string) {
  return request<{ companyId: string; clientId: string; page: number; pageSize: number; total: number; totalPages: number; items: ClientContactRecord[]; capabilities: ClientManagementCapabilities }>(
    `/api/ar-recovery/master-data/clients/${encodeURIComponent(clientId)}/contacts?page=1&pageSize=100`,
  );
}

export function createClientContact(clientId: string, input: ClientContactInput, idempotencyKey: string) {
  return request<{ contactId: string; clientId: string; status: string; version: number }>(
    `/api/ar-recovery/master-data/clients/${encodeURIComponent(clientId)}/contacts`,
    { method: "POST", headers: { "Content-Type": "application/json", "Idempotency-Key": idempotencyKey }, body: JSON.stringify(input) },
  );
}

export function updateClientContact(clientId: string, contactId: string, input: Partial<ClientContactInput> & { status?: "ACTIVE" | "INACTIVE" | "ARCHIVED"; expectedVersion: number }) {
  return request<{ contactId: string; clientId: string; status: string; version: number; correlationId: string }>(
    `/api/ar-recovery/master-data/clients/${encodeURIComponent(clientId)}/contacts/${encodeURIComponent(contactId)}`,
    { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(input) },
  );
}

export function listClientAddresses(clientId: string) {
  return request<{ companyId: string; clientId: string; items: ClientAddressRecord[]; capabilities: ClientManagementCapabilities }>(
    `/api/ar-recovery/master-data/clients/${encodeURIComponent(clientId)}/addresses`,
  );
}

export function createClientAddress(clientId: string, input: ClientAddressInput, idempotencyKey: string) {
  return request<{ addressId: string; clientId: string; addressType: string; status: string; version: number }>(
    `/api/ar-recovery/master-data/clients/${encodeURIComponent(clientId)}/addresses`,
    { method: "POST", headers: { "Content-Type": "application/json", "Idempotency-Key": idempotencyKey }, body: JSON.stringify(input) },
  );
}

export function updateClientAddress(clientId: string, addressId: string, input: Partial<ClientAddressInput> & { status?: "ACTIVE" | "INACTIVE" | "ARCHIVED"; expectedVersion: number }) {
  return request<{ addressId: string; clientId: string; addressType: string; status: string; version: number; correlationId: string }>(
    `/api/ar-recovery/master-data/clients/${encodeURIComponent(clientId)}/addresses/${encodeURIComponent(addressId)}`,
    { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(input) },
  );
}

export function getClientServiceConfiguration(clientId: string) {
  return request<{ companyId: string; clientId: string; configuration: ClientServiceConfiguration; capabilities: ClientManagementCapabilities }>(
    `/api/ar-recovery/master-data/clients/${encodeURIComponent(clientId)}/service-configuration`,
  );
}

export function updateClientServiceConfiguration(clientId: string, input: Omit<ClientServiceConfiguration, "configurationId" | "createdAt" | "updatedAt">) {
  return request<{ configurationId: string; clientId: string; version: number; correlationId: string }>(
    `/api/ar-recovery/master-data/clients/${encodeURIComponent(clientId)}/service-configuration`,
    { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(input) },
  );
}

export function getClientBrandingPortal(clientId: string) {
  return request<ClientBrandingPortalResult>(
    `/api/ar-recovery/master-data/clients/${encodeURIComponent(clientId)}/branding-portal`,
  );
}

export function updateClientBrandingPortal(clientId: string, input: ClientPresentationConfiguration) {
  const body = {
    letterPresentationReference: input.letterPresentationReference,
    statementPresentationReference: input.statementPresentationReference,
    senderDisplayReference: input.senderDisplayReference,
    portalPresentationReference: input.portalPresentationReference,
    expectedVersion: input.version,
  };
  return request<{ configurationId: string; clientId: string; version: number; correlationId: string }>(
    `/api/ar-recovery/master-data/clients/${encodeURIComponent(clientId)}/branding-portal`,
    { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
  );
}

export function listClientAssignments(clientId: string, page = 1, pageSize: 25 | 50 | 100 = 25, status = "") {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) }); if (status) params.set("status", status);
  return request<{ companyId: string; clientId: string; page: number; pageSize: number; total: number; totalPages: number; items: ClientAssignmentRecord[]; availableAssignees: AvailableClientAssignee[]; capabilities: ClientManagementCapabilities }>(
    `/api/ar-recovery/master-data/clients/${encodeURIComponent(clientId)}/assignments?${params}`,
  );
}

export function createClientAssignment(clientId: string, input: ClientAssignmentInput, idempotencyKey: string) {
  return request<{ assignmentId: string; clientId: string; status: string; version: number; correlationId: string }>(
    `/api/ar-recovery/master-data/clients/${encodeURIComponent(clientId)}/assignments`,
    { method: "POST", headers: { "Content-Type": "application/json", "Idempotency-Key": idempotencyKey }, body: JSON.stringify(input) },
  );
}

export function updateClientAssignment(clientId: string, assignmentId: string, input: Partial<ClientAssignmentInput> & { expectedVersion: number }) {
  return request<{ assignmentId: string; clientId: string; status: string; version: number; correlationId: string }>(
    `/api/ar-recovery/master-data/clients/${encodeURIComponent(clientId)}/assignments/${encodeURIComponent(assignmentId)}`,
    { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(input) },
  );
}

export function listClientNotes(clientId: string, page = 1, pageSize: 25 | 50 | 100 = 25, status = "", noteType = "") {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) }); if (status) params.set("status", status); if (noteType) params.set("noteType", noteType);
  return request<{ companyId: string; clientId: string; page: number; pageSize: number; total: number; totalPages: number; items: ClientNoteRecord[]; capabilities: ClientManagementCapabilities }>(
    `/api/ar-recovery/master-data/clients/${encodeURIComponent(clientId)}/notes?${params}`,
  );
}

export function createClientNote(clientId: string, input: ClientNoteInput, idempotencyKey: string) {
  return request<{ noteId: string; clientId: string; status: string; version: number; correlationId: string }>(
    `/api/ar-recovery/master-data/clients/${encodeURIComponent(clientId)}/notes`,
    { method: "POST", headers: { "Content-Type": "application/json", "Idempotency-Key": idempotencyKey }, body: JSON.stringify(input) },
  );
}

export function updateClientNote(clientId: string, noteId: string, input: Partial<ClientNoteInput> & { expectedVersion: number }) {
  return request<{ noteId: string; clientId: string; status: string; version: number; correlationId: string }>(
    `/api/ar-recovery/master-data/clients/${encodeURIComponent(clientId)}/notes/${encodeURIComponent(noteId)}`,
    { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(input) },
  );
}

export function listClientHistory(clientId: string, input: { page?: number; pageSize?: 25 | 50 | 100; direction?: "asc" | "desc"; event?: string; resource?: string } = {}) {
  const params = new URLSearchParams({ page: String(input.page || 1), pageSize: String(input.pageSize || 25), direction: input.direction || "desc" });
  if (input.event) params.set("event", input.event); if (input.resource) params.set("resource", input.resource);
  return request<{ companyId: string; clientId: string; page: number; pageSize: number; total: number; totalPages: number; direction: string; items: ClientHistoryRecord[]; capabilities: ClientManagementCapabilities }>(
    `/api/ar-recovery/master-data/clients/${encodeURIComponent(clientId)}/history?${params}`,
  );
}

export class ClientManagementApiError extends Error {
  constructor(message: string, public status: number, public code: string, public details?: unknown) { super(message); }
}

async function request<T>(url: string, init: RequestInit = {}) {
  const response = await fetch(url, { credentials: "same-origin", ...init });
  if (!response.ok) {
    const body = await response.text();
    let message = body.trim();
    let code = "CLIENT_MANAGEMENT_REQUEST_FAILED";
    let details: unknown;
    try {
      const parsed = JSON.parse(body) as { error?: string; code?: string; details?: unknown };
      message = String(parsed.error || message);
      code = String(parsed.code || code);
      details = parsed.details;
    } catch {
      // Plain-text errors are already safe to present.
    }
    throw new ClientManagementApiError(message || `Client Management request failed (${response.status}).`, response.status, code, details);
  }
  return response.json() as Promise<T>;
}
